import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, API_BASE, getToken } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { IoChevronDown } from 'react-icons/io5';

const SYNC_ETA_TEXT = 'Estimated time: a few seconds to 1 minute (depends on row count).';

const SAMPLE_PRODUCT_CSV = `Code,Name,Category,Size,Color,Material,Gender,Rent (1 day),Security deposit,Short description,Description,SEO title,Meta title,Meta keyphrase,Product size,Weight (grams),Main image
GFS-001,Silk Gown,Bridal,M,Red,Silk,Women,500,2000,"Evening gown, full length","Silk bridal gown with light embroidery. Ideal for receptions and formal events.","Rent Silk Gown Bridal | Gem Fashion","Silk Gown — Bridal Evening","silk gown bridal rental",M,420,gown-001.jpg
GFS-002,Wool Blazer,Blazers,L,Navy,Wool,Men,350,1500,"Tailored blazer, single-breasted","Navy wool blazer for business and events. Slim fit.","Wool Blazer Navy Rental | Gem Fashion","Navy Wool Blazer","wool blazer men rental",L,680,blazer-002.jpg`;

const SAMPLE_CATEGORY_CSV = `name,slug,sort_order,parent_name,image_url
Mens Wear,mens-wear,1,,
Womens Wear,womens-wear,2,,
Outerwear,outerwear,3,,
Accessories,accessories,4,,
Suits,suits,10,Mens Wear,
Ethnic Wear,ethnic-wear,11,Mens Wear,
Bridal,bridal,12,Womens Wear,
Jackets,jackets,20,Outerwear,
Blazers,blazers,21,Outerwear,
Shoes,shoes,30,Accessories,
Bags,bags,31,Accessories,`;

function downloadCsv(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const EMBEDDED_DRIVE_FOLDER_ID = '1wS32zv_C2Uys5imEmGEUXLWqFlkDjMVY';

const SYSTEM_FIELDS = [
  { key: 'code', label: 'Product code (required)', aliases: ['Code', 'SKU', 'Gemcode'] },
  { key: 'name', label: 'Product name', aliases: ['Name'] },
  { key: 'category', label: 'Category', aliases: ['Category'] },
  { key: 'size', label: 'Size', aliases: ['Size'] },
  { key: 'color', label: 'Color', aliases: ['Color'] },
  { key: 'material', label: 'Material', aliases: ['Material'] },
  { key: 'gender', label: 'Gender', aliases: ['Gender'] },
  { key: 'rent_1_day', label: 'Rent 1 day (INR)', aliases: ['Rent', 'Rent (1 day)', 'Rent 1 day'] },
  { key: 'security_deposit', label: 'Security deposit (INR)', aliases: ['Security deposit', 'Deposit'] },
  { key: 'short_description', label: 'Short description', aliases: ['Short description'] },
  { key: 'description', label: 'Description', aliases: ['Description'] },
  { key: 'seo_title', label: 'SEO title', aliases: ['SEO title'] },
  { key: 'meta_title', label: 'Meta title', aliases: ['Meta title'] },
  { key: 'meta_keyphrase', label: 'Meta keyphrase', aliases: ['Meta keyphrase'] },
  { key: 'product_size', label: 'Product size', aliases: ['Product size'] },
  { key: 'weight_grams', label: 'Weight (grams)', aliases: ['Weight', 'Weight (grams)'] },
  { key: 'image_1', label: 'Main image', aliases: ['Image', 'Main image', 'Image 1'] },
];

function parseCsvHeaders(text) {
  const str = (text || '').replace(/^\uFEFF/, '').trim();
  if (!str) return [];
  const lines = str.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) return [];
  const first = lines[0];
  const tabs = (first.match(/\t/g) || []).length;
  const semis = (first.match(/;/g) || []).length;
  const commas = (first.match(/,/g) || []).length;
  const delim = tabs > semis && tabs > commas ? '\t' : semis > commas ? ';' : ',';
  const parseRow = (line) => {
    const out = [];
    let cell = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') inQ = !inQ;
      else if (!inQ && ch === delim) { out.push(cell.trim()); cell = ''; }
      else cell += ch;
    }
    out.push(cell.trim());
    return out;
  };
  return parseRow(first);
}

function SummaryAlert({ result }) {
  if (!result) return null;
  if (result._stopped) {
    return <Alert severity="info" sx={{ mt: 2 }}>Sync stopped. Partial data may have been synced.</Alert>;
  }
  return (
    <>
      <Alert severity="success" sx={{ mb: 1 }}>
        Categories created: {result.categoriesCreated ?? 0}, matched: {result.categoriesMatched ?? 0}.
        Products created: {result.productsCreated ?? 0}, updated: {result.productsUpdated ?? 0}.
        {(result.duplicatesSkipped ?? 0) > 0 && (
          <> Duplicate rows skipped: {result.duplicatesSkipped}.</>
        )}
        {(result.productsCreated > 0 || result.productsUpdated > 0) && (
          <> <Link to="/admin/products">View products</Link> to see synced items.</>
        )}
      </Alert>
      {result.errors && result.errors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" component="span">Errors:</Typography>
          <Box component="ul" sx={{ margin: 0, pl: 2 }}>
            {result.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
            {result.errors.length > 20 && <li>… and {result.errors.length - 20} more</li>}
          </Box>
        </Alert>
      )}
    </>
  );
}

function SectionTitle({ children, first }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5, mt: first ? 0 : 2 }}>
      {children}
    </Typography>
  );
}

export default function AdminCsvSync() {
  const [loading, setLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [categories, setCategories] = useState([]);
  const [defaultCategoryId, setDefaultCategoryId] = useState('');
  const [imageBaseUrl, setImageBaseUrl] = useState('');
  const [imageDriveFolderId, setImageDriveFolderId] = useState(EMBEDDED_DRIVE_FOLDER_ID);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [mappingExpanded, setMappingExpanded] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api('/api/admin/sync/sheets/defaults')
      .then((d) => {
        if (d.image_drive_folder_id) setImageDriveFolderId(d.image_drive_folder_id);
      })
      .catch(() => {});
    api('/api/admin/sync/categories')
      .then((list) => setCategories(list || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const raw = csvFile ? null : (csvText && csvText.trim()) || '';
    if (raw) {
      const headers = parseCsvHeaders(raw);
      setCsvHeaders(headers);
      setColumnMapping((prev) => {
        const next = { ...prev };
        headers.forEach((h, i) => {
          const norm = String(h || '').trim().toLowerCase().replace(/\s+/g, ' ');
          SYSTEM_FIELDS.forEach((f) => {
            if (f.aliases.some((a) => norm === a.toLowerCase().replace(/\s+/g, ' '))) {
              next[f.key] = i;
            }
          });
        });
        return next;
      });
    } else if (csvFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const headers = parseCsvHeaders(reader.result || '');
        setCsvHeaders(headers);
        setColumnMapping((prev) => {
          const next = { ...prev };
          headers.forEach((h, i) => {
            const norm = String(h || '').trim().toLowerCase().replace(/\s+/g, ' ');
            SYSTEM_FIELDS.forEach((f) => {
              if (f.aliases.some((a) => norm === a.toLowerCase().replace(/\s+/g, ' '))) {
                next[f.key] = i;
              }
            });
          });
          return next;
        });
      };
      reader.readAsText(csvFile, 'utf-8');
    } else {
      setCsvHeaders([]);
      setColumnMapping({});
    }
  }, [csvFile, csvText]);

  const hasInput = csvFile || (csvText && csvText.trim().length > 0);

  const handleSync = async () => {
    if (!hasInput) {
      setError('Upload a CSV file or paste CSV text.');
      return;
    }
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const form = new FormData();
      if (csvFile) {
        form.append('file', csvFile);
      } else {
        form.append('csv_text', csvText.trim());
      }
      if (imageBaseUrl.trim()) form.append('image_base_url', imageBaseUrl.trim());
      if (imageDriveFolderId.trim()) form.append('image_drive_folder_id', imageDriveFolderId.trim());
      if (Object.keys(columnMapping).length > 0) {
        form.append('column_map', JSON.stringify(columnMapping));
      }
      if (defaultCategoryId && Number(defaultCategoryId) > 0) {
        form.append('default_category_id', defaultCategoryId);
      }

      const token = getToken();
      const res = await fetch(API_BASE + '/api/admin/sync/csv', {
        method: 'POST',
        headers: token ? { Authorization: 'Bearer ' + token } : {},
        body: form,
      });
      const data = res.ok ? await res.json().catch(() => ({})) : null;
      if (!res.ok) throw new Error(data && data.error ? data.error : res.statusText || 'CSV sync failed');
      setResult(data.summary || data);
    } catch (err) {
      setError(err.message || 'CSV sync failed');
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setCsvFile(null);
    setCsvText('');
    setCsvHeaders([]);
    setColumnMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const setMapping = (fieldKey, colIndex) => {
    setColumnMapping((prev) => {
      const next = { ...prev };
      if (colIndex === '' || colIndex == null) {
        delete next[fieldKey];
      } else {
        next[fieldKey] = Number(colIndex);
      }
      return next;
    });
  };

  return (
    <Card>
      {loading && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'primary.main', color: 'primary.contrastText', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, boxShadow: 2 }}>
          <LinearProgress sx={{ flex: 1, minWidth: 120, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
          <Typography variant="body2" fontWeight={600}>Syncing…</Typography>
        </Box>
      )}
      <CardHeader
        title="CSV Sync"
        subheader="Upload a CSV file or paste CSV text. Same column mapping and sync settings as Sheets sync."
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button size="small" variant="outlined" onClick={() => downloadCsv('sample-products.csv', SAMPLE_PRODUCT_CSV)}>
            Download product sample
          </Button>
          <Button size="small" variant="outlined" onClick={() => downloadCsv('sample-categories.csv', SAMPLE_CATEGORY_CSV)}>
            Download category sample
          </Button>
        </Box>

        <Accordion expanded={mappingExpanded} onChange={() => setMappingExpanded((e) => !e)} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<IoChevronDown />}>
            <Typography fontWeight={600}>Column mapping</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Map each system field to a CSV column. Upload or paste CSV first to see detected headers. Auto-mapping uses common names (Code, SKU, Name, Category, etc.).
            </Typography>
            {csvHeaders.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Upload a CSV file or paste CSV text above to enable column mapping.
              </Typography>
            ) : (
              <TableContainer sx={{ maxWidth: 720, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>System field</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>CSV column</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {SYSTEM_FIELDS.map((f) => (
                      <TableRow key={f.key}>
                        <TableCell>{f.label}</TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                              value={columnMapping[f.key] ?? ''}
                              displayEmpty
                              onChange={(e) => setMapping(f.key, e.target.value)}
                            >
                              <MenuItem value="">
                                <em>— Not mapped</em>
                              </MenuItem>
                              {csvHeaders.map((h, i) => (
                                <MenuItem key={i} value={i}>
                                  Col {i + 1}: {h || `(empty)`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>

        <SectionTitle first>1. CSV input</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Upload a .csv file or paste CSV content below. First row must be headers; data from row 2. Comma or semicolon separated.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button variant="outlined" component="label">
            Choose CSV file
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                setCsvFile(f || null);
                if (f) setCsvText('');
              }}
            />
          </Button>
          {csvFile && (
            <Typography variant="body2" color="text.secondary">
              {csvFile.name}
            </Typography>
          )}
          {(csvFile || csvText.trim()) && (
            <Button size="small" variant="outlined" color="secondary" onClick={clearFile}>
              Clear
            </Button>
          )}
        </Box>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={4}
          maxRows={12}
          label="Or paste CSV text"
          placeholder="Code,Name,Category,Size,Color,Rent,Security deposit&#10;SKU-001,Product One,Women,S,Red,500,1000"
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          sx={{ mb: 2 }}
          disabled={!!csvFile}
        />

        <Divider sx={{ my: 3 }} />

        <SectionTitle>2. Sync settings (same as Drive sync)</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Optional: image base URL or Drive folder ID if your CSV has image filenames. Assign all products to a category or use category from CSV.
          <strong>Rent</strong> and <strong>Security deposit</strong> are <strong>rupees</strong> (e.g. 1500 → ₹1,500 in the app; stored internally as paise).
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, maxWidth: 560, mb: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Assign to category</InputLabel>
            <Select
              value={defaultCategoryId}
              label="Assign to category"
              onChange={(e) => setDefaultCategoryId(e.target.value)}
            >
              <MenuItem value="">
                <em>Use category from CSV</em>
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label="Image base URL"
            placeholder="https://yoursite.com/images/"
            value={imageBaseUrl}
            onChange={(e) => setImageBaseUrl(e.target.value)}
          />
          <TextField
            size="small"
            fullWidth
            label="Drive folder ID (for images)"
            placeholder="From .../folders/FOLDER_ID"
            value={imageDriveFolderId}
            onChange={(e) => setImageDriveFolderId(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary">
            {SYNC_ETA_TEXT}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <SectionTitle>3. Run sync</SectionTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={loading || !hasInput}
          >
            {loading ? 'Syncing…' : 'Sync from CSV'}
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <SummaryAlert result={result} />
      </CardContent>
    </Card>
  );
}
